import { useDeferredRender } from '@/hooks/useDeferredRender';
import { Skeleton } from '@/components/ui/skeleton';

interface DeferredSectionProps {
  children: React.ReactNode;
  /** Custom delay in ms. If not provided, uses 2000ms on mobile, 500ms on desktop */
  delay?: number;
  /** Root margin for IntersectionObserver. Defaults to '200px' */
  rootMargin?: string;
  /** Show skeleton while loading. Defaults to true */
  showSkeleton?: boolean;
  /** Custom skeleton component */
  skeleton?: React.ReactNode;
  /** Disable IntersectionObserver. Defaults to false */
  disableIntersectionObserver?: boolean;
}

/**
 * Wrapper component that defers rendering of children until:
 * 1. Timeout has passed (2000ms on mobile, 500ms on desktop)
 * 2. Element is near viewport (using IntersectionObserver)
 * 
 * This significantly improves initial load performance on mobile
 */
export const DeferredSection = ({
  children,
  delay,
  rootMargin = '200px',
  showSkeleton = true,
  skeleton,
  disableIntersectionObserver = false
}: DeferredSectionProps) => {
  const { canRender, elementRef, isMobile } = useDeferredRender({
    delay,
    rootMargin,
    useIntersectionObserver: !disableIntersectionObserver
  });

  if (!canRender) {
    // Show minimal placeholder on mobile to reduce flicker
    if (isMobile) {
      return <div ref={elementRef} className="min-h-16 w-full" />;
    }
    
    // Show skeleton on desktop
    return (
      <div ref={elementRef} className="w-full">
        {showSkeleton && (skeleton || <Skeleton className="h-64 w-full" />)}
      </div>
    );
  }

  return <div ref={elementRef}>{children}</div>;
};
